import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Card,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Sidebar from "../../sidebar/sidebar";
import { BaseUrl } from "../../../Environment";
import { getSecureItem } from "../../../utilsFunctions";
import { findStaticScheme } from "./staticSchemes";
import axios from "axios";

const COMP_TYPES = [
  "Monthly",
  "Quarterly",
  "Half Yearly",
  "Yearly",
  "No Compounding",
];

const TOAST_OPTS = {
  position: "top-center",
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: false,
  pauseOnHover: false,
  draggable: false,
};

const INITIAL_FORM = {
  schemeType: "",
  interestRate: "",
  tenure: "",
  compType: "",
};

const staticToForm = (staticScheme) => ({
  ...INITIAL_FORM,
  schemeType: staticScheme.schemeType,
  ...staticScheme.defaults,
});

const useStyles = makeStyles((theme) => ({
  pageHeading: {
    textAlign: "center",
    marginTop: 15,
  },
  headingText: {
    fontWeight: 600,
    fontSize: "20px",
    color: "#3E4664",
    [theme.breakpoints.down("xs")]: {
      fontSize: "18px",
    },
  },
  wrapper: {
    marginLeft: "73px",
    marginTop: "3rem",
    padding: theme.spacing(0, 3),
    [theme.breakpoints.down("xs")]: {
      marginTop: "1.5rem",
      padding: theme.spacing(0, 1),
    },
  },
  card: {
    margin: theme.spacing(4),
    padding: theme.spacing(4),
    borderRadius: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  loaderWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  label: {
    fontWeight: 400,
    marginBottom: theme.spacing(1),
    color: "#8E8E98",
    fontSize: 14,
  },
  required: { color: "#e53935", marginLeft: 2 },
  textField: {
    [theme.breakpoints.up("xs")]: {
      width: "100%",
    },
    [theme.breakpoints.up("sm")]: {
      width: "80%",
    },
    "& .MuiOutlinedInput-root": {
      height: 35,
      borderRadius: 5,

      "& .MuiOutlinedInput-notchedOutline": {
        border: "1px solid #d4d4d5",
      },

      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d4d4d5",
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d4d4d5",
        borderWidth: 1,
      },
    },
  },
  actionButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      justifyContent: "center",
    },
  },
  cancelButton: {
    color: "#5a5a5a",
    border: "1px solid #D4D4D5",
    textTransform: "none",
    padding: theme.spacing(1, 4),
  },
  saveButton: {
    backgroundColor: "#13c902",
    color: "#fff",
    textTransform: "none",
    padding: theme.spacing(1, 4),
    "&:hover": {
      backgroundColor: "#10a802",
    },
    "&:disabled": {
      backgroundColor: "#a5e6a0",
      color: "#fff",
    },
  },
}));

const AddScheme = () => {
  const isMounted = useRef(true);

  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { schemeId: routeSchemeId } = useParams();
  const schemeId = routeSchemeId;

  const isEditMode = Boolean(schemeId);

  const presetName =
    new URLSearchParams(location.search).get("preset") ||
    location.state?.presetSchemeType;
  const presetStatic = !isEditMode ? findStaticScheme(presetName) : undefined;

  const [form, setForm] = useState(() =>
    presetStatic ? staticToForm(presetStatic) : INITIAL_FORM,
  );

  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditMode);

  const authHeaders = useCallback(
    () => ({
      "Content-Type": "application/json",
      Authorization: "Bearer " + getSecureItem("token"),
    }),
    [],
  );

  const handleChange = (field) => (event) => {
    event.persist();

    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const backLinkAction = () => {
    history.push("/schemes");
  };

  useEffect(() => {
    if (presetStatic) {
      setForm(staticToForm(presetStatic));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetStatic?.schemeType]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch existing scheme details when editing
  useEffect(() => {
    if (!isEditMode) return;

    const fetchScheme = async () => {
      setLoading(true);
      try {
        const resp = await axios.get(
          `${BaseUrl}/scheme/getSchemeById/${schemeId}`,
          { headers: authHeaders() },
        );

        const data = resp.data?.data || resp.data;

        if (isMounted.current && data) {
          setForm({
            schemeType: data.schemeType || "",
            interestRate: data.interestRate ?? "",
            tenure: data.tenure ?? "",
            compType: data.compType || "",
          });
        }
      } catch (err) {
        toast.error("Unable to fetch savings type details!", TOAST_OPTS);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    fetchScheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemeId, isEditMode]);

  const addScheme = async () => {
    const loggedinUser = getSecureItem("loggedinUser");

    const payload = {
      ...form,
      userId: loggedinUser.userId,
      companyId: loggedinUser.companyId,
    };

    try {
      setSaving(true);

      const resp = await axios.post(`${BaseUrl}/scheme/addScheme`, payload, {
        headers: authHeaders(),
      });

      if (resp.data?.success) {
        toast.success("Savings Type Added Successfully!", TOAST_OPTS);
        history.push("/schemes");
      } else {
        toast.error("Please try again later!", TOAST_OPTS);
      }
    } catch (err) {
      toast.error("Please try again later!", TOAST_OPTS);
    } finally {
      if (isMounted.current) {
        setSaving(false);
      }
    }
  };

  const updateScheme = async () => {
    const payload = { ...form };

    try {
      setSaving(true);
      const resp = await axios.put(
        `${BaseUrl}/scheme/updateScheme/${schemeId}`,
        payload,
        { headers: authHeaders() },
      );

      if (resp.data?.success) {
        toast.success("Savings Type Updated Successfully!", TOAST_OPTS);
        history.push("/schemes");
      } else {
        toast.error("Please try again later!", TOAST_OPTS);
      }
    } catch (err) {
      toast.error("Please try again later!", TOAST_OPTS);
    } finally {
      if (isMounted.current) {
        setSaving(false);
      }
    }
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    setSubmitted(true);

    const { schemeType, interestRate, tenure, compType } = form;

    if (schemeType && interestRate && tenure && compType) {
      isEditMode ? updateScheme() : addScheme();
    } else {
      toast.error("Please enter all data!", TOAST_OPTS);
    }
  };

  return (
    <div>
      <Sidebar />

      <div className={classes.pageHeading}>
        <Typography className={`${classes.headingText} Fonts`}>
          {isEditMode ? "Edit Savings Type" : "Add Savings Type"}
        </Typography>
      </div>

      <div className={classes.wrapper}>
        <Card className={classes.card}>
          {loading ? (
            <div className={classes.loaderWrapper}>
              <CircularProgress />
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography className={classes.label}>
                    Name
                    <span className={classes.required}>*</span>
                  </Typography>
                  <TextField
                    className={classes.textField}
                    fullWidth
                    variant="outlined"
                    value={form.schemeType}
                    error={submitted && !form.schemeType}
                    helperText={
                      submitted && !form.schemeType
                        ? "Savings type is required"
                        : ""
                    }
                    onChange={handleChange("schemeType")}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography className={classes.label}>
                    Interest Rate
                    <span className={classes.required}>*</span>
                  </Typography>
                  <TextField
                    className={classes.textField}
                    fullWidth
                    type="number"
                    variant="outlined"
                    value={form.interestRate}
                    inputProps={{ step: "0.01" }}
                    InputProps={{ endAdornment: "%" }}
                    error={submitted && !form.interestRate}
                    helperText={
                      submitted && !form.interestRate
                        ? "Interest rate is required"
                        : ""
                    }
                    onChange={handleChange("interestRate")}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography className={classes.label}>
                    Tenure
                    <span className={classes.required}>*</span>
                  </Typography>
                  <TextField
                    className={classes.textField}
                    fullWidth
                    type="number"
                    variant="outlined"
                    value={form.tenure}
                    error={submitted && !form.tenure}
                    helperText={
                      submitted && !form.tenure ? "Tenure is required" : ""
                    }
                    onChange={handleChange("tenure")}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography className={classes.label}>
                    Compounding Interest
                    <span className={classes.required}>*</span>
                  </Typography>
                  <TextField
                    select
                    className={classes.textField}
                    fullWidth
                    variant="outlined"
                    value={form.compType}
                    error={submitted && !form.compType}
                    helperText={
                      submitted && !form.compType
                        ? "Compounding type is required"
                        : ""
                    }
                    onChange={handleChange("compType")}
                  >
                    <MenuItem disabled value="">
                      <em>Select</em>
                    </MenuItem>
                    {COMP_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </form>
          )}
        </Card>

        <div className={classes.actionButtons}>
          <Button
            className={classes.cancelButton}
            onClick={backLinkAction}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            className={classes.saveButton}
            onClick={handleSubmit}
            disabled={saving || loading}
          >
            {saving ? (
              <CircularProgress size={18} style={{ color: "#fff" }} />
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddScheme;
